import { useState, type FormEvent } from "react";
import { LogOut } from "lucide-react";
import { actions, advanceCoverageClaim, loadSupplierPortal, settlementEvidenceUrl, submitObligationEvidence, uploadSupplierInvoice } from "@/lib/api";
import type { PurchaseOrder, PurchaseOrderMessage, Quote, SupplierAccess, SupplierFulfilmentUpdate, SupplierInvoiceSubmission } from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { useData, localDateTimeInputValue } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { SupplierTrustPanel } from "@/supplier-risk";
import { SupplierNetworkProfilePanel } from "@/supplier-network";

export function SupplierPortal({
  access,
  userName,
  onSignOut,
}: {
  access: SupplierAccess;
  userName: string;
  onSignOut: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const { data, error, loading } = useData(
    () => loadSupplierPortal(access),
    [access.organisation_id, access.vendor_id, refresh],
  );
  const [quoteRfq, setQuoteRfq] = useState<any>();
  const [invoicePo, setInvoicePo] = useState<PurchaseOrder>();
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const vendor = access.vendors;
  const organisation = access.organisations;
  const quoted = new Set(data.quotes.map((q) => q.rfq_id));
  const openObligations = data.obligations.filter(
    (item: any) => !["accepted", "waived", "cancelled"].includes(item.status),
  );
  const openClaims = data.claims.filter(
    (item: any) => !["closed", "rejected"].includes(item.state),
  );
  return (
    <div className="min-h-screen bg-[#f4f7f5]">
      <header className="border-b border-slate-200 bg-[#0b2834] px-5 py-5 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div>
            <RaqozLogo inverse />
            <p className="mt-2 text-xs text-white/50">
              SUPPLIER PORTAL · {organisation?.name}
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span>
              {userName} · {vendor?.name}
            </span>
            <Button variant="outline" size="sm" onClick={onSignOut}>
              <LogOut className="size-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto grid max-w-7xl gap-6 p-5 md:p-8">
        <PageHeader
          eyebrow="SUPPLIER WORKSPACE"
          title={"Welcome, " + (vendor?.name || "supplier")}
          description="Respond to sourcing opportunities, acknowledge orders, prove delivery and resolve warranty or service commitments."
        />
        <section className="boundary-panel" aria-labelledby="supplier-access-boundary">
          <div>
            <h2 id="supplier-access-boundary">Secure invited access · no supplier subscription required</h2>
            <p>
              Your buyer invitation is the only commercial prerequisite. The secure account attributes quotations, acknowledgements and evidence to the invited supplier; it does not require a paid RAQOZ plan.
            </p>
          </div>
        </section>
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <Panel>
            <small className="text-slate-500">Open RFQs</small>
            <strong className="mt-2 block font-serif text-3xl">
              {
                data.rfqs.filter(
                  (r: any) => r.status === "published" && !quoted.has(r.id),
                ).length
              }
            </strong>
          </Panel>
          <Panel>
            <small className="text-slate-500">Purchase orders</small>
            <strong className="mt-2 block font-serif text-3xl">
              {data.orders.length}
            </strong>
          </Panel>
          <Panel>
            <small className="text-slate-500">Open obligations</small>
            <strong className="mt-2 block font-serif text-3xl">
              {openObligations.length}
            </strong>
          </Panel>
          <Panel>
            <small className="text-slate-500">Coverage records</small>
            <strong className="mt-2 block font-serif text-3xl">
              {data.coverages.length}
            </strong>
          </Panel>
          <Panel>
            <small className="text-slate-500">Open claims</small>
            <strong className="mt-2 block font-serif text-3xl">
              {openClaims.length}
            </strong>
          </Panel>
          <Panel>
            <small className="text-slate-500">Invoices submitted</small>
            <strong className="mt-2 block font-serif text-3xl">
              {data.invoiceSubmissions.length}
            </strong>
          </Panel>
        </section>
        <SupplierNetworkProfilePanel
          organisationId={access.organisation_id}
          vendorId={access.vendor_id}
          workspace={data.supplierNetwork}
          onChanged={() => setRefresh((value) => value + 1)}
        />
        <SupplierTrustPanel
          organisationId={access.organisation_id}
          vendorId={access.vendor_id}
          workspace={data.supplierTrust}
          onChanged={() => setRefresh((value) => value + 1)}
        />
        {data.messages.length > 0 && (
          <Panel>
            <h2 className="font-serif text-xl">Company bulletins</h2>
            <div className="mt-3 grid gap-3">
              {data.messages.map((message) => (
                <button
                  key={message.id}
                  className="rounded-xl border border-slate-200 p-4 text-left"
                  onClick={async () => {
                    if (!message.read_at) {
                      await actions.markSupplierMessageRead(
                        access.organisation_id,
                        message.id,
                      );
                      setRefresh((v) => v + 1);
                    }
                  }}
                >
                  <strong className="text-sm">{message.subject}</strong>
                  <p className="mt-1 text-sm text-slate-600">{message.body}</p>
                  <small className="mt-2 block text-slate-400">
                    {displayDate(message.created_at, true)} ·{" "}
                    {message.read_at ? "Read" : "New"}
                  </small>
                </button>
              ))}
            </div>
          </Panel>
        )}
        <section>
          <h2 className="mb-3 font-serif text-2xl">Open quotation requests</h2>
          {data.rfqs.filter((r: any) => r.status === "published").length ? (
            <>
            <div className="hidden md:block">
            <Table
              heads={["RFQ", "Requirement", "Deadline", "Status", "Action"]}
            >
              {data.rfqs
                .filter((r: any) => r.status === "published")
                .map((row: any) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.rfq_no}</strong>
                    </td>
                    <td>
                      {row.title}
                      <small className="block text-slate-500">
                        {row.requisitions?.requisition_lines?.length || 0} lines
                      </small>
                    </td>
                    <td>{displayDate(row.deadline)}</td>
                    <td>
                      <Badge
                        value={quoted.has(row.id) ? "submitted" : "published"}
                      />
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant={quoted.has(row.id) ? "outline" : "default"}
                        onClick={() => setQuoteRfq(row)}
                      >
                        {quoted.has(row.id) ? "Revise quote" : "Submit quote"}
                      </Button>
                    </td>
                  </tr>
                ))}
            </Table>
            </div>
            <div className="grid gap-3 md:hidden" aria-label="Open quotation requests">
              {data.rfqs
                .filter((row: any) => row.status === "published")
                .map((row: any) => (
                  <article key={row.id} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="text-xs font-bold tracking-[.08em] text-slate-500">{row.rfq_no}</span>
                        <h3 className="mt-1 font-semibold">{row.title}</h3>
                      </div>
                      <Badge value={quoted.has(row.id) ? "submitted" : "published"} />
                    </div>
                    <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div><dt className="text-xs text-slate-500">Deadline</dt><dd className="mt-1">{displayDate(row.deadline)}</dd></div>
                      <div><dt className="text-xs text-slate-500">Requirement</dt><dd className="mt-1">{row.requisitions?.requisition_lines?.length || 0} lines</dd></div>
                    </dl>
                    <Button
                      className="mt-4 min-h-11 w-full"
                      variant={quoted.has(row.id) ? "outline" : "default"}
                      onClick={() => setQuoteRfq(row)}
                    >
                      {quoted.has(row.id) ? "Revise quote" : "Submit quote"}
                    </Button>
                  </article>
                ))}
            </div>
            </>
          ) : (
            <Empty
              title="No open RFQs"
              description="New invitations and company requirement bulletins appear here."
            />
          )}
        </section>
        <section className="grid gap-5 xl:grid-cols-2">
          <Panel>
            <h2 className="font-serif text-xl">Awarded orders</h2>
            <p className="mt-1 text-sm text-slate-500">
              Acknowledge each PO before fulfilment and keep its promised dates
              current.
            </p>
            <div className="mt-3 grid gap-2">
              {data.orders.map((po) => (
                <SupplierOrderCard
                  key={po.id}
                  po={po}
                  organisationId={access.organisation_id}
                  acknowledgement={data.acknowledgements.find(
                    (a: any) =>
                      a.commercial_instruments?.source_purchase_order_id ===
                      po.id,
                  )}
                  payment={data.payments.find(
                    (p: any) => p.purchase_order_id === po.id,
                  )}
                  messages={data.orderMessages.filter(
                    (message) => message.purchase_order_id === po.id,
                  )}
                  fulfilment={data.fulfilmentUpdates.filter(
                    (item) =>
                      item.commercial_instruments?.source_purchase_order_id ===
                      po.id,
                  )}
                  invoiceSubmissions={data.invoiceSubmissions.filter(
                    (item) => item.purchase_order_id === po.id,
                  )}
                  onSubmitInvoice={() => setInvoicePo(po)}
                  onDone={() => setRefresh((v) => v + 1)}
                />
              ))}
              {!data.orders.length && (
                <p className="text-sm text-slate-500">
                  No purchase orders yet.
                </p>
              )}
            </div>
          </Panel>
          <Panel>
            <h2 className="font-serif text-xl">Active contracts</h2>
            <div className="mt-3 grid gap-2">
              {data.contracts
                .filter((c) => c.status === "active")
                .map((c) => (
                  <div key={c.id} className="rounded-xl bg-slate-50 p-3">
                    <strong className="text-sm">
                      {c.contract_no} · {c.title}
                    </strong>
                    <small className="block text-slate-500">
                      {c.contract_items?.length || 0} rate items · {c.currency}
                    </small>
                  </div>
                ))}
              {!data.contracts.some((c) => c.status === "active") && (
                <p className="text-sm text-slate-500">No active contracts.</p>
              )}
            </div>
          </Panel>
        </section>
        <Panel>
          <h2 className="font-serif text-xl">Remittance and payment proof</h2>
          <p className="mt-1 text-sm text-slate-500">Only evidence explicitly shared by the buyer appears here. Internal bank statements remain private.</p>
          {data.settlementEvidence.length?<div className="mt-4 overflow-x-auto"><Table heads={["PO / invoice","Reference","Date","Amount","Proof"]}>{data.settlementEvidence.map((row:any)=><tr key={row.evidence_id}><td><strong>{row.po_no||row.schedule_title}</strong><small className="block text-slate-500">{row.invoice_no||titleCase(row.evidence_type)}</small></td><td>{row.external_reference}</td><td>{displayDate(row.settlement_date)}</td><td>{money(row.amount,row.currency)}</td><td>{row.object_path?<Button size="sm" variant="outline" onClick={async()=>{try{window.open(await settlementEvidenceUrl(row.object_path),"_blank","noopener");}catch(reason){window.alert((reason as Error).message);}}}>View remittance</Button>:"Reference supplied"}</td></tr>)}</Table></div>:<p className="mt-4 text-sm text-slate-500">No buyer-shared settlement evidence yet.</p>}
        </Panel>
        <section className="grid gap-5 xl:grid-cols-[1.15fr_.85fr]">
          <Panel>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl">Post-award obligations</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Submit evidence against delivery, milestone, SLA, maintenance
                  and entitlement promises.
                </p>
              </div>
              <Badge
                value={openObligations.length ? "action required" : "current"}
              />
            </div>
            <div className="mt-4 grid gap-3">
              {data.obligations.map((item: any) => (
                <SupplierObligationCard
                  key={item.id}
                  item={item}
                  organisationId={access.organisation_id}
                  onDone={() => setRefresh((v) => v + 1)}
                />
              ))}
              {!data.obligations.length && (
                <p className="text-sm text-slate-500">
                  No obligations assigned.
                </p>
              )}
            </div>
          </Panel>
          <Panel>
            <h2 className="font-serif text-xl">Warranty, AMC & guarantees</h2>
            <div className="mt-4 grid gap-3">
              {data.coverages.map((item: any) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-slate-200 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <strong className="text-sm">
                        {item.coverage_reference}
                      </strong>
                      <small className="block text-slate-500">
                        {titleCase(item.coverage_type)} ·{" "}
                        {item.assets?.asset_tag || "Linked asset"}
                      </small>
                    </div>
                    <Badge value={item.status} />
                  </div>
                  <small className="mt-2 block text-slate-500">
                    Valid {displayDate(item.starts_on)} –{" "}
                    {displayDate(item.ends_on)}
                  </small>
                </div>
              ))}
              {!data.coverages.length && (
                <p className="text-sm text-slate-500">
                  No coverage records assigned.
                </p>
              )}
            </div>
            <h3 className="mt-6 text-sm font-semibold uppercase tracking-[.16em] text-slate-500">
              Claims
            </h3>
            <div className="mt-3 grid gap-3">
              {data.claims.map((item: any) => (
                <SupplierClaimCard
                  key={item.id}
                  item={item}
                  organisationId={access.organisation_id}
                  onDone={() => setRefresh((v) => v + 1)}
                />
              ))}
              {!data.claims.length && (
                <p className="text-sm text-slate-500">No claims.</p>
              )}
            </div>
          </Panel>
        </section>
      </main>
      <SupplierQuoteDialog
        rfq={quoteRfq}
        existingQuote={data.quotes.find((quote) => quote.rfq_id === quoteRfq?.id && quote.is_current !== false)}
        access={access}
        onOpenChange={(open) => !open && setQuoteRfq(undefined)}
        onDone={() => setRefresh((v) => v + 1)}
      />
      <SupplierInvoiceDialog
        purchaseOrder={invoicePo}
        access={access}
        onOpenChange={(open) => !open && setInvoicePo(undefined)}
        onDone={() => setRefresh((v) => v + 1)}
      />
    </div>
  );
}

function SupplierObligationCard({
  item,
  organisationId,
  onDone,
}: {
  item: any;
  organisationId: string;
  onDone: () => void;
}) {
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const closed = ["accepted", "waived", "cancelled"].includes(item.status);
  async function submit() {
    if (!summary.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      await submitObligationEvidence(
        organisationId,
        item.id,
        "supplier_declaration",
        summary.trim(),
        { submitted_from: "supplier_portal" },
      );
      setSummary("");
      setMessage("Evidence submitted for buyer review.");
      onDone();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Unable to submit evidence.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="text-sm">{item.title}</strong>
          <small className="block text-slate-500">
            {titleCase(item.kind)} · due {displayDate(item.due_at, true)}
          </small>
        </div>
        <Badge value={item.status} />
      </div>
      <p className="mt-2 text-sm text-slate-600">{item.description}</p>
      {!closed && (
        <div className="mt-3 grid gap-2">
          <Textarea
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            placeholder="Evidence summary, document reference or completion details"
          />
          <Button size="sm" disabled={busy || !summary.trim()} onClick={submit}>
            {busy ? "Submitting…" : "Submit fulfilment evidence"}
          </Button>
        </div>
      )}
      {message && (
        <p className="mt-2 text-xs text-slate-600" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

function SupplierClaimCard({
  item,
  organisationId,
  onDone,
}: {
  item: any;
  organisationId: string;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const next: Record<string, { state: string; label: string }> = {
    supplier_notified: { state: "acknowledged", label: "Acknowledge claim" },
    acknowledged: { state: "diagnosis", label: "Submit diagnosis" },
    diagnosis: {
      state: "repair_or_replacement",
      label: "Propose repair / replacement",
    },
  };
  const action = next[item.state];
  async function advance() {
    if (!action) return;
    setBusy(true);
    setMessage("");
    try {
      await advanceCoverageClaim(
        organisationId,
        item.id,
        action.state,
        note.trim(),
      );
      setNote("");
      setMessage("Claim updated.");
      onDone();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Unable to update claim.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="text-sm">{item.issue_summary}</strong>
          <small className="block text-slate-500">
            {item.asset_coverages?.coverage_reference || "Coverage claim"}
          </small>
        </div>
        <Badge value={item.state} />
      </div>
      {action && (
        <div className="mt-3 grid gap-2">
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Response, diagnosis or proposed remedy"
          />
          <Button size="sm" disabled={busy} onClick={advance}>
            {busy ? "Updating…" : action.label}
          </Button>
        </div>
      )}
      {message && (
        <p className="mt-2 text-xs text-slate-600" role="status">
          {message}
        </p>
      )}
    </div>
  );
}

function SupplierQuoteDialog({
  rfq,
  existingQuote,
  access,
  onOpenChange,
  onDone,
}: {
  rfq?: any;
  existingQuote?: Quote;
  access: SupplierAccess;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lines = rfq?.requisitions?.requisition_lines || [];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const quoteLines = lines
      .filter((line: any) => form.get(`include-${line.id}`))
      .map((line: any) => ({
        requisition_line_id: line.id,
        quantity: Number(form.get(`qty-${line.id}`)),
        unit_price: Number(form.get(`price-${line.id}`)),
        complies: Boolean(form.get(`complies-${line.id}`)),
        remarks: String(form.get(`remarks-${line.id}`) || ""),
      }));
    setBusy(true);
    setError("");
    try {
      const payload = {
        p_rfq_id: rfq.id,
        p_vendor_id: access.vendor_id,
        p_quote_no: form.get("quoteNo"),
        p_valid_until: form.get("validUntil"),
        p_delivery_days: Number(form.get("deliveryDays")),
        p_commercial_mode: form.get("mode"),
        p_currency: String(form.get("currency")).toUpperCase(),
        p_exchange_rate_to_base: Number(form.get("rate")),
        p_lines: quoteLines,
      };
      if (existingQuote)
        await actions.reviseSupplierQuote(access.organisation_id, payload);
      else await actions.submitSupplierQuote(access.organisation_id, payload);
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(rfq)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="SUPPLIER QUOTATION"
          title={`Respond to ${rfq?.rfq_no || "RFQ"}`}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Quote number
              <Input name="quoteNo" defaultValue={existingQuote?.quote_no} required />
            </Label>
            <Label>
              Valid until
              <Input name="validUntil" type="date" defaultValue={existingQuote?.valid_until} required />
            </Label>
            <Label>
              Delivery days
              <Input
                name="deliveryDays"
                type="number"
                min="0"
                defaultValue={existingQuote?.delivery_days ?? 7}
                required
              />
            </Label>
            <Label>
              Currency
              <Input
                name="currency"
                defaultValue={existingQuote?.currency || access.vendors?.default_currency || "INR"}
                pattern="[A-Za-z]{3}"
                maxLength={3}
                required
              />
            </Label>
            <Label>
              Exchange rate to buyer base
              <Input
                name="rate"
                type="number"
                min="0.00000001"
                step="0.00000001"
                defaultValue={existingQuote?.exchange_rate_to_base ?? 1}
                required
              />
            </Label>
            <Label>
              Commercial mode
              <Select name="mode" defaultValue={existingQuote?.commercial_mode || "partial"}>
                <option value="partial">Partial line award permitted</option>
                <option value="all_or_nothing">All lines or no award</option>
              </Select>
            </Label>
          </div>
          <div className="grid gap-3 rounded-xl bg-slate-50 p-4">
            {lines.map((line: any) => {
              const prior = existingQuote?.quote_lines?.find(
                (item) => item.requisition_line_id === line.id,
              );
              return (
              <div
                key={line.id}
                className="grid gap-2 border-b border-slate-200 pb-3 md:grid-cols-[auto_1.5fr_.5fr_.7fr_auto_1fr]"
              >
                <label className="flex items-center gap-2 text-xs">
                  <input
                    name={`include-${line.id}`}
                    type="checkbox"
                    defaultChecked={Boolean(prior) || !existingQuote}
                  />
                  Include
                </label>
                <div>
                  <strong className="text-sm">{line.name}</strong>
                  <small className="block text-slate-500">
                    {line.specification}
                  </small>
                </div>
                <Input
                  aria-label="Quantity"
                  name={`qty-${line.id}`}
                  type="number"
                  defaultValue={prior?.quantity ?? line.quantity}
                  max={line.quantity}
                  min="0.01"
                  step="0.01"
                />
                <Input
                  aria-label="Unit price"
                  name={`price-${line.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={prior?.unit_price}
                  required
                />
                <label className="flex items-center gap-2 text-xs">
                  <input
                    name={`complies-${line.id}`}
                    type="checkbox"
                    defaultChecked={prior?.complies ?? true}
                  />
                  Meets spec
                </label>
                <Input
                  aria-label="Remarks"
                  name={`remarks-${line.id}`}
                  placeholder="Remarks"
                  defaultValue={prior?.remarks || ""}
                />
              </div>
              );
            })}
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              {existingQuote ? "Submit revised quotation" : "Submit quotation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SupplierOrderCard({
  po,
  organisationId,
  acknowledgement,
  payment,
  messages,
  fulfilment,
  invoiceSubmissions,
  onSubmitInvoice,
  onDone,
}: {
  po: PurchaseOrder;
  organisationId: string;
  acknowledgement: any;
  payment: any;
  messages: PurchaseOrderMessage[];
  fulfilment: SupplierFulfilmentUpdate[];
  invoiceSubmissions: SupplierInvoiceSubmission[];
  onSubmitInvoice: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  async function accept() {
    setBusy(true);
    try {
      await actions.acknowledgePurchaseOrder(organisationId, po.id, "accepted");
      onDone();
    } catch (reason) {
      window.alert((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function postMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await actions.postPurchaseOrderMessage(
        organisationId,
        po.id,
        reply.trim(),
      );
      setReply("");
      onDone();
    } catch (reason) {
      setUpdateMessage((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function submitFulfilment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setUpdateMessage("");
    try {
      await actions.submitSupplierFulfilmentUpdate(organisationId, po.id, {
        p_kind: form.get("kind"),
        p_occurred_at: new Date(String(form.get("occurredAt"))).toISOString(),
        p_reference: String(form.get("reference") || "") || null,
        p_performed_by: form.get("performedBy"),
        p_note: form.get("note"),
      });
      event.currentTarget.reset();
      setUpdateMessage("Fulfilment evidence submitted for buyer verification.");
      onDone();
    } catch (reason) {
      setUpdateMessage((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const latestInvoice = invoiceSubmissions[0];
  const maySubmitInvoice = !latestInvoice || ["rejected", "returned"].includes(latestInvoice.status);
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <div>
        <strong className="text-sm">{po.po_no}</strong>
        <span className="float-right text-sm">{money(po.total, po.currency)}</span>
        <small className="block text-slate-500">
          {titleCase(po.status)}
          {payment ? " · Payment " + titleCase(payment.state) : ""}
        </small>
        {po.status === "issued" && !acknowledgement && (
          <Button className="mt-2" size="sm" disabled={busy} onClick={accept}>
            Acknowledge & accept
          </Button>
        )}
        {acknowledgement && (
          <small className="mt-2 block text-emerald-700">
            Acknowledged · {titleCase(acknowledgement.response)}
          </small>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
        <Button size="sm" variant="outline" disabled={!maySubmitInvoice} onClick={onSubmitInvoice}>
          {!latestInvoice ? "Submit invoice" : maySubmitInvoice ? "Resubmit invoice" : latestInvoice.status === "accepted" ? "Invoice accepted" : "Invoice under review"}
        </Button>
        {latestInvoice && (
          <Badge value={`invoice ${latestInvoice.status}`} />
        )}
        {fulfilment.length > 0 && (
          <Badge value={`${fulfilment.filter((item) => item.state === "submitted").length} fulfilment pending`} />
        )}
      </div>
      <details className="mt-3 border-t border-slate-200 pt-3">
        <summary className="cursor-pointer text-sm font-semibold">
          Post fulfilment update
        </summary>
        <form className="mt-3 grid gap-2" onSubmit={submitFulfilment}>
          <Select name="kind" defaultValue="goods_dispatch">
            <option value="goods_dispatch">Goods dispatched</option>
            <option value="service_entry">Service performed</option>
            <option value="milestone">Milestone reached</option>
            <option value="installation">Installation</option>
            <option value="commissioning">Commissioning</option>
          </Select>
          <Input name="occurredAt" type="datetime-local" defaultValue={localDateTimeInputValue()} required />
          <Input name="performedBy" placeholder="Performed by" maxLength={200} required />
          <Input name="reference" placeholder="Dispatch / service reference (optional)" maxLength={200} />
          <Textarea name="note" placeholder="Evidence and completion details" maxLength={2000} required />
          <Button size="sm" disabled={busy} type="submit">Submit for buyer verification</Button>
        </form>
      </details>
      <details className="mt-3 border-t border-slate-200 pt-3">
        <summary className="cursor-pointer text-sm font-semibold">
          Order conversation · {messages.length}
        </summary>
        <div className="mt-3 max-h-48 space-y-2 overflow-auto">
          {messages.map((message) => (
            <div key={message.id} className="rounded-lg bg-white p-2 text-sm">
              <small className="block text-slate-500">
                {titleCase(message.sender_kind)} · {displayDate(message.created_at, true)}
              </small>
              {message.body}
            </div>
          ))}
          {!messages.length && <p className="text-sm text-slate-500">No messages.</p>}
        </div>
        <form className="mt-2 grid gap-2" onSubmit={postMessage}>
          <Textarea value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Reply about this order" maxLength={4000} />
          <Button size="sm" disabled={busy || !reply.trim()} type="submit">Send to buyer</Button>
        </form>
      </details>
      {updateMessage && <p className="mt-2 text-xs text-slate-600" role="status">{updateMessage}</p>}
    </div>
  );
}

function SupplierInvoiceDialog({
  purchaseOrder,
  access,
  onOpenChange,
  onDone,
}: {
  purchaseOrder?: PurchaseOrder;
  access: SupplierAccess;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!purchaseOrder) return;
    const form = new FormData(event.currentTarget);
    const file = form.get("invoiceFile");
    if (!(file instanceof File) || !file.size) {
      setError("Original invoice evidence is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await uploadSupplierInvoice(access, purchaseOrder.id, file, {
        invoiceNo: String(form.get("invoiceNo")),
        invoiceDate: String(form.get("invoiceDate")),
        subtotal: Number(form.get("subtotal")),
        taxAmount: Number(form.get("taxAmount")),
        currency: String(form.get("currency")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(purchaseOrder)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="SUPPLIER INVOICE"
          title={`Submit against ${purchaseOrder?.po_no || "purchase order"}`}
          description="The original file and SHA-256 hash are preserved. Buyer finance must review it before matching."
        />
        <form className="grid gap-4 p-6" onSubmit={submit}>
          <div className="grid gap-4 md:grid-cols-2">
            <Label>Invoice number<Input name="invoiceNo" required /></Label>
            <Label>Invoice date<Input name="invoiceDate" type="date" required /></Label>
            <Label>Subtotal<Input name="subtotal" type="number" min="0.01" step="0.01" required /></Label>
            <Label>Tax amount<Input name="taxAmount" type="number" min="0" step="0.01" defaultValue="0" required /></Label>
            <Label>Currency<Input name="currency" defaultValue={purchaseOrder?.currency || access.vendors?.default_currency || "INR"} pattern="[A-Za-z]{3}" required /></Label>
            <Label>Original invoice file<Input name="invoiceFile" type="file" accept="application/pdf,image/png,image/jpeg" required /></Label>
          </div>
          <p className="text-xs text-slate-500">
            OCR extraction is not configured in this release; finance will review the entered fields against the immutable original.
          </p>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={busy} type="submit">{busy ? "Submitting…" : "Submit invoice"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
